import ArrowForwardOutlined from '@mui/icons-material/ArrowForwardOutlined';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { attributePillSx, CHECKBOX_SX, Pill, PRIMARY_PILL_SX } from './Pill.js';
import {
  formatScore,
  type Recommendation,
  type RowOutcome,
  type TagChange,
} from './recommendationRows.js';

const CHANGE_COLUMNS = 'minmax(0, max-content) minmax(0, max-content) 16px minmax(0, max-content)';

const COLUMN_LABEL_SX = {
  fontSize: 10,
  fontWeight: 600,
  color: 'text.disabled',
};

function impactSx(up: boolean) {
  return {
    bgcolor: up ? 'success.bg' : 'error.bg',
    color: up ? 'success.main' : 'error.main',
  };
}

interface RecommendationCardProps {
  recommendation: Recommendation;
  pick?: { checked: boolean; onToggle: () => void };
  // Inside a card the rows are inset from its edge; in the drawer they line up
  // with the sections above them.
  flush?: boolean;
  // The first row under a heading is already separated by it.
  ruled?: boolean;
  // On the workflow page the reasoning folds away, closed, behind a "Why" toggle. The drawer
  // and the printed brief always show it in full, with nothing to click.
  collapsibleWhy?: boolean;
}

function Why({ why, collapsible }: { why: string; collapsible: boolean }): ReactNode {
  const [open, setOpen] = useState(false);
  const text = (
    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
      <Box component="span" sx={{ fontWeight: 600 }}>
        Why:{' '}
      </Box>
      {why}
    </Typography>
  );
  if (!collapsible) {
    return (
      <Stack direction="row" sx={{ gap: 1, alignItems: 'flex-start' }}>
        <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', mt: 0.25, flexShrink: 0 }} />
        {text}
      </Stack>
    );
  }
  return (
    <Stack sx={{ gap: 1, alignItems: 'flex-start' }}>
      <ButtonBase
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        sx={{ gap: 0.5, borderRadius: 0.5, fontSize: 12, fontWeight: 600, color: 'text.secondary' }}
      >
        <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled' }} />
        Why
        <ExpandMoreOutlined
          sx={{
            fontSize: 16,
            color: 'text.disabled',
            transition: 'transform .15s ease',
            transform: open ? 'rotate(180deg)' : 'none',
          }}
        />
      </ButtonBase>
      <Collapse in={open} unmountOnExit sx={{ alignSelf: 'stretch' }}>
        {text}
      </Collapse>
    </Stack>
  );
}

function Change({ change }: { change: TagChange }): ReactNode {
  return (
    <>
      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{change.feature}</Typography>
      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{change.from ?? '—'}</Typography>
      <ArrowForwardOutlined sx={{ fontSize: 14, color: 'text.disabled' }} />
      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{change.to}</Typography>
    </>
  );
}

export function RecommendationCard({
  recommendation,
  pick,
  flush = false,
  ruled = true,
  collapsibleWhy = false,
}: RecommendationCardProps): ReactNode {
  const why = [recommendation.rationale, ...recommendation.evidence]
    .filter((part) => part !== null)
    .join(' · ');

  return (
    <Stack
      data-testid={`recommendation-${recommendation.key}`}
      sx={{
        gap: 2,
        px: flush ? 0 : 4,
        py: 3,
        ...(ruled ? { borderTop: '1px solid', borderColor: 'divider' } : {}),
      }}
    >
      <Stack
        direction="row"
        sx={{ gap: 2, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}
      >
        <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center', minWidth: 0 }}>
          {pick === undefined ? null : (
            <Checkbox
              checked={pick.checked}
              onChange={pick.onToggle}
              size="small"
              sx={CHECKBOX_SX}
              slotProps={{ input: { 'aria-label': recommendation.title } }}
            />
          )}
          {recommendation.action === null ? null : (
            <Pill sx={PRIMARY_PILL_SX}>{recommendation.action.toUpperCase()}</Pill>
          )}
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{recommendation.title}</Typography>
          {recommendation.attribute === null ? null : (
            <Pill sx={attributePillSx(recommendation.attribute)}>
              {recommendation.attribute.toUpperCase()}
            </Pill>
          )}
        </Stack>
        {recommendation.impact === null ? null : (
          <Pill sx={impactSx(recommendation.impactUp)}>
            {recommendation.impactUp ? '↑' : '↓'} {recommendation.impact}
          </Pill>
        )}
      </Stack>

      {recommendation.changes.length === 0 ? null : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: CHANGE_COLUMNS,
            columnGap: 5,
            rowGap: 1.5,
            alignItems: 'center',
          }}
        >
          <Typography sx={COLUMN_LABEL_SX}>ELEMENT</Typography>
          <Typography sx={COLUMN_LABEL_SX}>CURRENT</Typography>
          <Box />
          <Typography sx={COLUMN_LABEL_SX}>RECOMMENDED</Typography>
          {recommendation.changes.map((change) => (
            <Change
              key={`${change.action}:${change.feature}:${change.from}:${change.to}`}
              change={change}
            />
          ))}
        </Box>
      )}

      {why === '' ? null : <Why why={why} collapsible={collapsibleWhy} />}
    </Stack>
  );
}

export function RecommendationScores({ outcome }: { outcome: RowOutcome }): ReactNode {
  const parts = [
    `Overall ${formatScore(outcome.score)}`,
    outcome.bestPracticeScore === null
      ? null
      : `best practice ${formatScore(outcome.bestPracticeScore)}`,
    outcome.performanceScore === null
      ? null
      : `performance ${formatScore(outcome.performanceScore)}`,
    outcome.brandGuidelinesScore === null
      ? null
      : `brand guidelines ${formatScore(outcome.brandGuidelinesScore)}`,
  ].filter((part) => part !== null);

  return (
    <Typography
      data-testid="recommendation-scores"
      sx={{ px: 4, pb: 2, fontSize: 12, color: 'text.secondary' }}
    >
      {parts.join(' · ')}
    </Typography>
  );
}

export function RecommendationFailure({ error }: { error: string | null }): ReactNode {
  return (
    <Alert severity="error" data-testid="recommendation-failure" sx={{ mx: 4, my: 3 }}>
      <AlertTitle>No recommendations for this creative</AlertTitle>
      {error}
    </Alert>
  );
}
